import React, { useState } from 'react';
import {
  Plus, Trash2, Settings, Save, X, Database, Globe,
  FileText, Copy, ChevronDown, ChevronRight, Key,
  Hash as VariableIcon, Lock, Search
} from 'lucide-react';

export interface Variable {
  id: string;
  name: string;
  value: any;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array' | 'null';
  scope: 'global' | 'flow' | 'local' | 'environment';
  description?: string;
  isSecret?: boolean;
  isReadOnly?: boolean;
  defaultValue?: any;
  validation?: {
    required?: boolean;
    min?: number;
    max?: number;
    pattern?: string;
    enum?: any[];
  };
}

interface VariableManagerProps {
  variables: Variable[];
  onVariableAdd?: (variable: Variable) => void;
  onVariableUpdate?: (id: string, variable: Partial<Variable>) => void;
  onVariableDelete?: (id: string) => void;
  onVariableDuplicate?: (id: string) => void;
  isReadOnly?: boolean;
}

export const VariableManager: React.FC<VariableManagerProps> = ({
  variables = [],
  onVariableAdd,
  onVariableUpdate,
  onVariableDelete,
  onVariableDuplicate,
  isReadOnly = false
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Variable>>({});
  const [expandedScopes, setExpandedScopes] = useState<Set<string>>(new Set(['global']));
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newVariable, setNewVariable] = useState<Partial<Variable>>({
    name: '',
    value: '',
    type: 'string',
    scope: 'flow',
    description: ''
  });

  const toggleScope = (scope: string) => {
    const newExpanded = new Set(expandedScopes);
    if (newExpanded.has(scope)) {
      newExpanded.delete(scope);
    } else {
      newExpanded.add(scope);
    }
    setExpandedScopes(newExpanded);
  };

  const detectType = (value: any): Variable['type'] => {
    if (value === null || value === 'null') return 'null';
    if (Array.isArray(value)) return 'array';
    if (typeof value === 'object') return 'object';
    if (typeof value === 'boolean' || value === 'true' || value === 'false') return 'boolean';
    if (!isNaN(Number(value)) && value !== '') return 'number';
    return 'string';
  };

  const parseValue = (value: string, type: Variable['type']): any => {
    try {
      switch (type) {
        case 'number':
          return Number(value);
        case 'boolean':
          return value === 'true' || value === '1';
        case 'object':
        case 'array':
          return JSON.parse(value);
        case 'null':
          return null;
        default:
          return value;
      }
    } catch {
      return value;
    }
  };

  const formatValue = (value: any, isSecret?: boolean): string => {
    if (isSecret) return '••••••••';
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return '[Object]';
      }
    }
    return String(value);
  };

  const handleEdit = (variable: Variable) => {
    setEditingId(variable.id);
    setEditForm({
      ...variable,
      value: formatValue(variable.value, false)
    });
  };

  const handleSave = () => {
    if (editingId && onVariableUpdate) {
      const parsedValue = parseValue(editForm.value as string, editForm.type!);
      onVariableUpdate(editingId, {
        ...editForm,
        value: parsedValue
      });
      setEditingId(null);
      setEditForm({});
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleAddVariable = () => {
    if (newVariable.name && onVariableAdd) {
      const parsedValue = parseValue(newVariable.value as string, newVariable.type!);
      onVariableAdd({
        id: `var-${Date.now()}`,
        name: newVariable.name,
        value: parsedValue,
        type: newVariable.type || 'string',
        scope: newVariable.scope || 'flow',
        description: newVariable.description,
        isSecret: newVariable.isSecret,
        isReadOnly: newVariable.isReadOnly
      } as Variable);
      
      setNewVariable({
        name: '',
        value: '',
        type: 'string',
        scope: 'flow',
        description: ''
      });
      setShowAddForm(false);
    }
  };

  const getScopeIcon = (scope: Variable['scope']) => {
    switch (scope) {
      case 'global':
        return <Globe size={14} />;
      case 'environment':
        return <Database size={14} />;
      case 'flow':
        return <FileText size={14} />;
      case 'local':
        return <VariableIcon size={14} />;
      default:
        return null;
    }
  };

  const getScopeColor = (scope: Variable['scope']) => {
    switch (scope) {
      case 'global':
        return '#3B82F6';
      case 'environment':
        return '#10B981';
      case 'flow':
        return '#F59E0B';
      case 'local':
        return '#8B5CF6';
      default:
        return '#6B7280';
    }
  };

  const filteredVariables = variables.filter(variable => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      variable.name.toLowerCase().includes(query) ||
      variable.description?.toLowerCase().includes(query) ||
      formatValue(variable.value).toLowerCase().includes(query)
    );
  });

  const groupedVariables = filteredVariables.reduce((acc, variable) => {
    if (!acc[variable.scope]) {
      acc[variable.scope] = [];
    }
    acc[variable.scope].push(variable);
    return acc;
  }, {} as Record<string, Variable[]>);

  const scopeOrder: Variable['scope'][] = ['global', 'environment', 'flow', 'local'];

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: '#ffffff',
      color: '#374151'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #d1d5db',
        background: '#f9fafb'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '14px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Database size={16} />
            Gerenciador de Variáveis
          </h3>
          {!isReadOnly && (
            <button
              onClick={() => setShowAddForm(true)}
              style={{
                padding: '6px 12px',
                background: '#3B82F6',
                border: 'none',
                borderRadius: '6px',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Plus size={14} />
              Nova Variável
            </button>
          )}
        </div>

        {/* Search */}
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{
            position: 'absolute',
            left: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#64748b'
          }} />
          <input
            type="text"
            placeholder="Buscar variáveis..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '6px 6px 6px 32px',
              background: '#ffffff',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              color: '#374151',
              fontSize: '12px',
              outline: 'none'
            }}
          />
        </div>
      </div>

      {/* Add Variable Form */}
      {showAddForm && (
        <div style={{
          padding: '16px',
          borderBottom: '1px solid #d1d5db',
          background: '#f9fafb'
        }}>
          <h4 style={{
            margin: '0 0 12px 0',
            fontSize: '12px',
            fontWeight: 600
          }}>
            Nova Variável
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <input
              type="text"
              placeholder="Nome da variável"
              value={newVariable.name}
              onChange={(e) => setNewVariable({ ...newVariable, name: e.target.value })}
              style={{
                padding: '6px',
                background: '#ffffff',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                color: '#374151',
                fontSize: '12px',
                outline: 'none'
              }}
            />
            <input
              type="text"
              placeholder="Valor inicial"
              value={newVariable.value}
              onChange={(e) => {
                const detectedType = detectType(e.target.value);
                setNewVariable({ 
                  ...newVariable, 
                  value: e.target.value,
                  type: detectedType
                });
              }}
              style={{
                padding: '6px',
                background: '#ffffff',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                color: '#374151',
                fontSize: '12px',
                outline: 'none'
              }}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <select
                value={newVariable.scope}
                onChange={(e) => setNewVariable({ ...newVariable, scope: e.target.value as Variable['scope'] })}
                style={{
                  flex: 1,
                  padding: '6px',
                  background: '#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  color: '#374151',
                  fontSize: '12px',
                  outline: 'none'
                }}
              >
                <option value="global">Global</option>
                <option value="environment">Ambiente</option>
                <option value="flow">Fluxo</option>
                <option value="local">Local</option>
              </select>
              <select
                value={newVariable.type}
                onChange={(e) => setNewVariable({ ...newVariable, type: e.target.value as Variable['type'] })}
                style={{
                  flex: 1,
                  padding: '6px',
                  background: '#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  color: '#374151',
                  fontSize: '12px',
                  outline: 'none'
                }}
              >
                <option value="string">String</option>
                <option value="number">Número</option>
                <option value="boolean">Booleano</option>
                <option value="object">Objeto</option>
                <option value="array">Array</option>
                <option value="null">Null</option>
              </select>
            </div>
            <input
              type="text"
              placeholder="Descrição (opcional)"
              value={newVariable.description}
              onChange={(e) => setNewVariable({ ...newVariable, description: e.target.value })}
              style={{
                padding: '6px',
                background: '#ffffff',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                color: '#374151',
                fontSize: '12px',
                outline: 'none'
              }}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={newVariable.isSecret}
                  onChange={(e) => setNewVariable({ ...newVariable, isSecret: e.target.checked })}
                />
                <Lock size={12} />
                Segredo
              </label>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={newVariable.isReadOnly}
                  onChange={(e) => setNewVariable({ ...newVariable, isReadOnly: e.target.checked })}
                />
                <Key size={12} />
                Somente Leitura
              </label>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAddForm(false)}
                style={{
                  padding: '6px 12px',
                  background: '#6B7280',
                  border: 'none',
                  borderRadius: '4px',
                  color: '#ffffff',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleAddVariable}
                style={{
                  padding: '6px 12px',
                  background: '#10B981',
                  border: 'none',
                  borderRadius: '4px',
                  color: '#ffffff',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Adicionar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Variables List */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '12px'
      }}>
        {scopeOrder.map(scope => {
          const scopeVariables = groupedVariables[scope];
          if (!scopeVariables || scopeVariables.length === 0) return null;

          return (
            <div key={scope} style={{ marginBottom: '16px' }}>
              <div
                onClick={() => toggleScope(scope)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px',
                  background: '#f9fafb',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  marginBottom: '8px'
                }}
              >
                {expandedScopes.has(scope) ?
                  <ChevronDown size={14} /> :
                  <ChevronRight size={14} />
                }
                {getScopeIcon(scope)}
                <span style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'capitalize',
                  color: getScopeColor(scope)
                }}>
                  {scope === 'environment' ? 'Ambiente' : 
                   scope === 'global' ? 'Global' :
                   scope === 'flow' ? 'Fluxo' : 'Local'}
                </span>
                <span style={{
                  marginLeft: 'auto',
                  fontSize: '10px',
                  color: '#64748b',
                  background: '#ffffff',
                  padding: '2px 6px',
                  borderRadius: '10px'
                }}>
                  {scopeVariables.length}
                </span>
              </div>

              {expandedScopes.has(scope) && (
                <div style={{ paddingLeft: '8px' }}>
                  {scopeVariables.map(variable => (
                    <div
                      key={variable.id}
                      style={{
                        padding: '10px',
                        background: '#f9fafb',
                        border: `1px solid ${editingId === variable.id ? '#3B82F6' : '#d1d5db'}`,
                        borderRadius: '6px',
                        marginBottom: '6px'
                      }}
                    >
                      {editingId === variable.id ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            style={{
                              padding: '6px',
                              background: '#ffffff',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              color: '#374151',
                              fontSize: '12px',
                              outline: 'none'
                            }}
                          />
                          <input
                            type="text"
                            value={editForm.value}
                            onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                            style={{
                              padding: '6px',
                              background: '#ffffff',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              color: '#374151',
                              fontSize: '12px',
                              outline: 'none',
                              fontFamily: 'monospace'
                            }}
                          />
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button
                              onClick={handleCancel}
                              style={{
                                padding: '4px 8px',
                                background: '#6B7280',
                                border: 'none',
                                borderRadius: '4px',
                                color: '#ffffff',
                                fontSize: '11px',
                                cursor: 'pointer'
                              }}
                            >
                              <X size={12} />
                            </button>
                            <button
                              onClick={handleSave}
                              style={{
                                padding: '4px 8px',
                                background: '#10B981',
                                border: 'none',
                                borderRadius: '4px',
                                color: '#ffffff',
                                fontSize: '11px',
                                cursor: 'pointer'
                              }}
                            >
                              <Save size={12} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '6px'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <span style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#3B82F6'
                              }}>
                                {variable.name}
                              </span>
                              {variable.isSecret && <Lock size={10} color="#EF4444" />}
                              {variable.isReadOnly && <Key size={10} color="#F59E0B" />}
                              <span style={{
                                fontSize: '10px',
                                color: '#64748b',
                                background: '#ffffff',
                                padding: '2px 4px',
                                borderRadius: '4px'
                              }}>
                                {variable.type}
                              </span>
                            </div>
                            {!isReadOnly && !variable.isReadOnly && (
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button
                                  onClick={() => handleEdit(variable)}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#6b7280',
                                    padding: '2px'
                                  }}
                                >
                                  <Settings size={12} />
                                </button>
                                <button
                                  onClick={() => navigator.clipboard.writeText(formatValue(variable.value))}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#6b7280',
                                    padding: '2px'
                                  }}
                                >
                                  <Copy size={12} />
                                </button>
                                {onVariableDuplicate && (
                                  <button
                                    onClick={() => onVariableDuplicate(variable.id)}
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      color: '#6b7280',
                                      padding: '2px'
                                    }}
                                  >
                                    <Copy size={12} />
                                  </button>
                                )}
                                {onVariableDelete && (
                                  <button
                                    onClick={() => onVariableDelete(variable.id)}
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      color: '#EF4444',
                                      padding: '2px'
                                    }}
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          <pre style={{
                            margin: 0,
                            padding: '6px',
                            background: '#ffffff',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontFamily: 'monospace',
                            overflow: 'auto',
                            maxHeight: '60px',
                            color: variable.isSecret ? '#6B7280' : '#e2e8f0'
                          }}>
                            {formatValue(variable.value, variable.isSecret)}
                          </pre>
                          {variable.description && (
                            <div style={{
                              marginTop: '4px',
                              fontSize: '10px',
                              color: '#64748b'
                            }}>
                              {variable.description}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VariableManager;
